import * as React from "react";
import { Button, Input, Dropdown, Table, Modal, Header, Segment, Icon, Checkbox, Label, Popup, Tab, Message } from "semantic-ui-react";
import AceEditor from "react-ace";
import { CustomDiff } from "../../configChange/configEdit/diff/CustomDiff";
import { TransportOrderJsonViewer } from "../../configChange/AiScripter/TransportOrderJsonViewer";
import {AiParseResult, fetchTestFailures, TestFailureLogEntry} from "./TestFailuresApi";
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/mode-json';
import {continueAiChat} from "../../docViewer/aiParseResultsHandler/aiRequestsApi";

const LIMIT_OPTIONS = [
    { key: '10', text: '10', value: 10 },
    { key: '25', text: '25', value: 25 },
    { key: '50', text: '50', value: 50 },
    { key: '100', text: '100', value: 100 },
    { key: '250', text: '250', value: 250 },
    { key: '1000', text: '1000 (Slow)', value: 1000 },
];

const MODEL_OPTIONS = [
    'GPT35',
    'GPT4',
    'GPT4_TURBO',
    'GPT4_O',
    'GPT4_O_Mini',
    'GPT5',
    'GPT5_MINI',
    'Claude37_Sonnet',
    'Claude4_Sonnet',
    'Claude45_Sonnet',
    'Claude45_Haiku',
    'DeepSeek_V3',
    'GEMINI25_PRO',
    'GEMINI25_FLASH',
    'GPT5_NANO',
    'CLAUDE45_OPUS'
];

interface Attachment {
    name: string;
    uploadOnlyBase64AttachmentContent: string;
}

interface GroupedFailure {
    testName: string;
    latestFailure: TestFailureLogEntry;
    count24h: number;
    count7d: number;
    totalInView: number;
}

interface ConversationMessage {
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
    promptTokens?: number;
    completionTokens?: number;
    price?: number;
}

const BASE64_PLACEHOLDER = "<BASE64_CONTENT_HIDDEN>";

function cleanJsonForDisplay(jsonString: string): string {
    if (!jsonString) return "";
    try {
        const obj = JSON.parse(jsonString);
        const stripBase64 = (item: any) => {
            if (item && typeof item === 'object') {
                if (Array.isArray(item)) {
                    item.forEach(i => stripBase64(i));
                } else {
                    if ('uploadOnlyBase64AttachmentContent' in item) {
                        item.uploadOnlyBase64AttachmentContent = BASE64_PLACEHOLDER;
                    }
                    Object.values(item).forEach(val => stripBase64(val));
                }
            }
        };
        stripBase64(obj);
        return JSON.stringify(obj, null, 2);
    } catch (e) {
        return jsonString.replace(
            /"uploadOnlyBase64AttachmentContent"\s*:\s*"[^"]*"/g,
            `"uploadOnlyBase64AttachmentContent": "${BASE64_PLACEHOLDER}"`
        );
    }
}

function extractAttachments(jsonString: string): Attachment[] {
    try {
        const parsed = JSON.parse(jsonString);
        const attachments: Attachment[] = [];
        function traverse(obj: any) {
            if (obj && typeof obj === 'object') {
                if (Array.isArray(obj)) {
                    obj.forEach(item => traverse(item));
                } else {
                    if (obj.uploadOnlyBase64AttachmentContent && obj.name) {
                        if (obj.uploadOnlyBase64AttachmentContent !== BASE64_PLACEHOLDER &&
                            obj.uploadOnlyBase64AttachmentContent.length > 100) {
                            attachments.push({
                                name: obj.name,
                                uploadOnlyBase64AttachmentContent: obj.uploadOnlyBase64AttachmentContent
                            });
                        }
                    }
                    Object.values(obj).forEach(val => traverse(val));
                }
            }
        }
        traverse(parsed);
        return attachments;
    } catch (e) {
        return [];
    }
}

function base64ToBlob(base64: string): Blob {
    try {
        if (!base64 || base64 === BASE64_PLACEHOLDER || base64.length < 100) {
            throw new Error("Invalid or missing Base64 content");
        }
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        return new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
    } catch (e) {
        console.error("Failed to convert base64 to blob", e);
        return new Blob([], { type: 'application/pdf' });
    }
}

function mapModelToEnum(modelString: string): string {
    const mapping: Record<string, string> = {
        'gpt-3.5-turbo': 'GPT35',
        'gpt-4': 'GPT4',
        'gpt-4o': 'GPT4_O',
        'gpt-5': 'GPT5',
        'gpt-5-mini': 'GPT5_MINI',
        'gpt-4o-mini': 'GPT4_O_Mini',
        'gpt-4-turbo': 'GPT4_TURBO',
        'deepseek-chat': 'DeepSeek_V3',
        'claude-3-5-sonnet-20241022': 'Claude35_Sonnet',
        'claude-sonnet-4-5-20250929': 'Claude45_Sonnet',
        'claude-haiku-4-5-20251001': 'Claude45_Haiku',
        'claude-sonnet-4-20250514': 'Claude4_Sonnet',
        'gemini-2.5-pro': 'GEMINI25_PRO',
        'gemini-2.5-flash': 'GEMINI25_FLASH',
        'claude-3-7-sonnet-20250219': 'Claude37_Sonnet',
        'gpt-5-nano': 'GPT5_NANO',
        'claude-opus-4-5-20251101': 'CLAUDE45_OPUS',
        'claude-sonnet-4-6':'CLAUDE46_SONNET',
        'claude-opus-4-6':'CLAUDE46_OPUS',
    };
    return mapping[modelString] || modelString;
}

const PdfViewer = React.memo(({ attachment }: { attachment: Attachment }) => {
    const url = React.useMemo(
        () => URL.createObjectURL(base64ToBlob(attachment.uploadOnlyBase64AttachmentContent)),
        [attachment.uploadOnlyBase64AttachmentContent]
    );

    React.useEffect(() => {
        return () => URL.revokeObjectURL(url);
    }, [url]);

    if (!attachment.uploadOnlyBase64AttachmentContent ||
        attachment.uploadOnlyBase64AttachmentContent === BASE64_PLACEHOLDER ||
        attachment.uploadOnlyBase64AttachmentContent.length < 100) {
        return (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'red' }}>
                <Icon name="warning sign" /> Content not available (Placeholder detected)
            </div>
        );
    }

    return (
        <iframe
            src={url}
            title={attachment.name}
            style={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#f4f4f4' }}
        />
    );
});

const JsonEditor = React.memo(({ content }: { content: string }) => {
    const cleanedContent = React.useMemo(() => cleanJsonForDisplay(content), [content]);

    return (
        <AceEditor
            mode="json"
            theme="monokai"
            value={cleanedContent}
            readOnly={true}
            width="100%"
            height="100%"
            showPrintMargin={false}
            fontSize={14}
            wrapEnabled={true}
            setOptions={{ useWorker: false, wrap: true }}
        />
    );
});

interface AiProcessingDetailsModalProps {
    open: boolean;
    onClose: () => void;
    expectedData?: AiParseResult[] | null;
    actualData?: AiParseResult[] | null;
}

function AiProcessingDetailsModal({ open, onClose, expectedData, actualData }: AiProcessingDetailsModalProps) {
    const safeExpectedData = expectedData ?? [];
    const safeActualData = actualData ?? [];

    const [activeTab, setActiveTab] = React.useState<'actual' | 'expected'>('actual');
    const [selectedEntry, setSelectedEntry] = React.useState<AiParseResult | null>(null);
    const [detailViewOpen, setDetailViewOpen] = React.useState(false);

    const [chatOpen, setChatOpen] = React.useState(false);
    const [conversation, setConversation] = React.useState<ConversationMessage[]>([]);
    const [userInput, setUserInput] = React.useState("");
    const [selectedModel, setSelectedModel] = React.useState("");
    const [originalModel, setOriginalModel] = React.useState("");
    const [conversationLoading, setConversationLoading] = React.useState(false);
    const chatEndRef = React.useRef<HTMLDivElement>(null);
    const [prettifiedOutput, setPrettifiedOutput] = React.useState("");
    const [isPrettified, setIsPrettified] = React.useState(false);
    const [jsonFormatError, setJsonFormatError] = React.useState("");

    function extractJson(text: string): string {
        const markdownJsonMatch = text.match(/```json\s*([\s\S]*?)\s*```/);
        if (markdownJsonMatch) {
            return markdownJsonMatch[1].trim();
        }

        const codeBlockMatch = text.match(/```\s*([\s\S]*?)\s*```/);
        if (codeBlockMatch) {
            return codeBlockMatch[1].trim();
        }

        const jsonObjectMatch = text.match(/{[\s\S]*}/);
        if (jsonObjectMatch) {
            return jsonObjectMatch[0];
        }

        const jsonArrayMatch = text.match(/\[[\s\S]*]/);
        if (jsonArrayMatch) {
            return jsonArrayMatch[0];
        }

        return text;
    }

    function prettifyJson(text: string): string {
        const extracted = extractJson(text);
        const parsed = JSON.parse(extracted);
        return JSON.stringify(parsed, null, 2);
    }

    function handlePrettifyOutput() {
        if (!selectedEntry) return;

        try {
            const prettified = prettifyJson(selectedEntry.rawOutput || selectedEntry.output);
            setPrettifiedOutput(prettified);
            setJsonFormatError("");
            setIsPrettified(true);
        } catch (e) {
            setJsonFormatError("Invalid JSON format. Cannot Prettify JSON");
            setIsPrettified(false);
        }
    }

    function handleShowOriginal() {
        setPrettifiedOutput("");
        setIsPrettified(false);
        setJsonFormatError("");
    }

    React.useEffect(() => {
        if (chatOpen && chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [conversation]);

    function openChat(entry: AiParseResult) {
        const modelEnum = mapModelToEnum(entry.model || '');
        setSelectedModel(modelEnum);
        setOriginalModel(modelEnum);

        const initialConversation: ConversationMessage[] = [
            {
                role: 'user',
                content: entry.input || '',
                timestamp: new Date().toISOString()
            },
            {
                role: 'assistant',
                content: entry.rawOutput || entry.output || '',
                timestamp: new Date().toISOString(),
                promptTokens: entry.promptTokens,
                completionTokens: entry.completionTokens,
                price: entry.totalPrice
            }
        ];

        setConversation(initialConversation);
        setChatOpen(true);
        setUserInput("");
    }

    async function sendMessage() {
        if (!userInput.trim()) return;

        const newMessage: ConversationMessage = {
            role: 'user',
            content: userInput,
            timestamp: new Date().toISOString()
        };

        const updatedConversation = [...conversation, newMessage];
        setConversation(updatedConversation);
        setUserInput("");
        setConversationLoading(true);

        try {
            const messages = updatedConversation.map(msg => ({
                role: msg.role,
                content: msg.content
            }));

            const conversationJson = JSON.stringify(messages);
            const response = await continueAiChat(conversationJson, selectedModel);

            const assistantMessage: ConversationMessage = {
                role: 'assistant',
                content: response.output,
                timestamp: new Date().toISOString(),
                promptTokens: response.usageTokensNumber,
                completionTokens: response.completionTokensNumber,
                price: response.totalPromptPrice
            };

            setConversation([...updatedConversation, assistantMessage]);
        } catch (e: any) {
            console.error("Failed to send message:", e);
            alert(`Error: ${e.message}`);
        } finally {
            setConversationLoading(false);
        }
    }

    function copyToClipboard(text: string) {
        navigator.clipboard.writeText(text);
    }

    function renderTable(entries: AiParseResult[], label: string) {
        const safeEntries = entries || [];

        return (
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <Header size="small" style={{ margin: 0 }}>
                        {label} ({safeEntries.length} entries)
                    </Header>
                </div>

                <Table celled compact size="large">
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell width={4}>Entry ID</Table.HeaderCell>
                            <Table.HeaderCell width={4}>Type</Table.HeaderCell>
                            <Table.HeaderCell width={4}>Model</Table.HeaderCell>
                            <Table.HeaderCell width={2}>Tokens (I/O)</Table.HeaderCell>
                            <Table.HeaderCell width={2}>Price</Table.HeaderCell>
                            <Table.HeaderCell width={3}>Cache Hits</Table.HeaderCell>
                            <Table.HeaderCell width={2}>Actions</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {safeEntries.length > 0 ? safeEntries.map((entry, idx) => (
                            <Table.Row key={`${entry._id || idx}-${idx}`}>
                                <Table.Cell style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                                    {entry._id || '-'}
                                </Table.Cell>
                                <Table.Cell>{entry.type || '-'}</Table.Cell>
                                <Table.Cell>{entry.actuallyUsedModel || entry.model || '-'}</Table.Cell>
                                <Table.Cell>
                                    {entry.promptTokens ?? '-'} / {entry.completionTokens ?? '-'}
                                </Table.Cell>
                                <Table.Cell>
                                    {entry.totalPrice != null ? `$${entry.totalPrice.toFixed(4)}` : '-'}
                                </Table.Cell>
                                <Table.Cell>
                                    {entry.counter != null && entry.counter > 0 && (
                                        <Label size="tiny" color="green">
                                            {entry.counter}x
                                        </Label>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <Button.Group size="mini">
                                        <Button
                                            icon
                                            onClick={() => {
                                                setSelectedEntry(entry);
                                                setDetailViewOpen(true);
                                            }}
                                            disabled={!entry.input}
                                            title="View Details"
                                        >
                                            <Icon name="eye" />
                                        </Button>
                                        <Button
                                            icon
                                            primary
                                            onClick={() => openChat(entry)}
                                            disabled={!entry.input}
                                            title="Open Chat"
                                        >
                                            <Icon name="comments" />
                                        </Button>
                                    </Button.Group>
                                </Table.Cell>
                            </Table.Row>
                        )) : (
                            <Table.Row>
                                <Table.Cell colSpan={7} textAlign="center">
                                    No AI processing data available
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </div>
        );
    }

    function renderChatView() {
        const totals = conversation.reduce((acc, msg) => {
            if (msg.role === 'assistant') {
                return {
                    totalInput: acc.totalInput + (msg.promptTokens || 0),
                    totalOutput: acc.totalOutput + (msg.completionTokens || 0),
                    totalPrice: acc.totalPrice + (msg.price || 0)
                };
            }
            return acc;
        }, { totalInput: 0, totalOutput: 0, totalPrice: 0 });

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '90%', maxHeight: '100hv-200px' }}>
                <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button size="small" onClick={() => setChatOpen(false)}>
                        <Icon name="arrow left" /> Back to Table
                    </Button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <Dropdown
                            selection
                            fluid
                            options={MODEL_OPTIONS.map(m => ({ key: m, text: m, value: m }))}
                            value={selectedModel}
                            onChange={(_, data) => setSelectedModel(data.value as string)}
                        />
                    </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '15px', backgroundColor: '#fafafa' }}>
                    {conversation.map((msg, idx) => (
                        <div
                            key={idx}
                            style={{
                                marginBottom: '12px',
                                padding: '12px',
                                borderRadius: '6px',
                                backgroundColor: msg.role === 'user' ? '#e3f2fd' : '#f1f8e9',
                                border: msg.role === 'user' ? '1px solid #2196f3' : '1px solid #8bc34a',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <strong style={{ color: msg.role === 'user' ? '#1976d2' : '#558b2f', fontSize: '0.85em' }}>
                                    {msg.role.toUpperCase()}
                                </strong>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '0.8em' }}>
                                    {msg.promptTokens && <span>In: {msg.promptTokens}</span>}
                                    {msg.completionTokens && <span>Out: {msg.completionTokens}</span>}
                                    {msg.price && <span>${msg.price.toFixed(4)}</span>}
                                    <Icon name="copy" link onClick={() => copyToClipboard(msg.content)} />
                                </div>
                            </div>
                            <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.9em' }}>
                                {msg.content}
                            </div>
                        </div>
                    ))}
                    <div ref={chatEndRef} />
                </div>

                <div style={{ padding: '10px', borderTop: '1px solid #ddd' }}>
                    <Input
                        fluid
                        placeholder="Type your message..."
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                            }
                        }}
                        action={
                            <Button
                                primary
                                onClick={sendMessage}
                                loading={conversationLoading}
                                disabled={!userInput.trim()}
                            >
                                Send
                            </Button>
                        }
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            <Modal open={open} onClose={onClose} size="large">
                <Header style={{ padding: '12px 15px', backgroundColor: '#2c3e50', color: '#fff', margin: 0 }}>
                    <Icon name="microchip" /> AI Processing Details
                </Header>
                <Modal.Content style={{ minHeight: '400px', maxHeight:"100hv-200px", padding: chatOpen ? 0 : '15px' }}>
                    {chatOpen ? renderChatView() : (
                        <>
                            <div style={{ marginBottom: '15px' }}>
                                <Button.Group>
                                    <Button
                                        primary={activeTab === 'actual'}
                                        onClick={() => setActiveTab('actual')}
                                    >
                                        Actual ({safeActualData.length})
                                    </Button>
                                    <Button
                                        primary={activeTab === 'expected'}
                                        onClick={() => setActiveTab('expected')}
                                    >
                                        Expected ({safeExpectedData.length})
                                    </Button>
                                </Button.Group>
                            </div>

                            {activeTab === 'actual' && renderTable(safeActualData, 'Actual AI Processing')}
                            {activeTab === 'expected' && renderTable(safeExpectedData, 'Expected AI Processing')}
                        </>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={onClose}>Close</Button>
                </Modal.Actions>
            </Modal>

            <Modal
                open={detailViewOpen}
                onClose={() => {
                    setDetailViewOpen(false);
                    setPrettifiedOutput("");
                    setIsPrettified(false);
                    setJsonFormatError("");
                }}
                size="fullscreen"
            >
                <Header style={{ padding: '12px 15px', backgroundColor: '#34495e', color: '#fff', margin: 0 }}>
                    {selectedEntry?.type || 'Details'} - {selectedEntry?._id || 'N/A'}
                </Header>
                <Modal.Content style={{ height: 'calc(100vh - 350px)', padding: '15px' }}>
                    {selectedEntry && (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            minHeight: 0,
                            gap: '15px'
                        }}>
                            {/* Info Section */}
                            <Segment style={{ padding: '10px', flexShrink: 0 }}>
                                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '0.95em' }}>
                                    <div><strong>Type:</strong> {selectedEntry.type || '-'}</div>
                                    <div><strong>Model:</strong> {selectedEntry.actuallyUsedModel || selectedEntry.model || '-'}</div>
                                    <div><strong>Prompt Tokens:</strong> {selectedEntry.promptTokens ?? '-'}</div>
                                    <div><strong>Completion Tokens:</strong> {selectedEntry.completionTokens ?? '-'}</div>
                                    <div><strong>Price:</strong> {selectedEntry.totalPrice != null ? `$${selectedEntry.totalPrice.toFixed(4)}` : '-'}</div>
                                    {selectedEntry.counter != null && selectedEntry.counter > 0 && (
                                        <div><strong>Cache Hits:</strong> <Label size="tiny" color="green">{selectedEntry.counter}x</Label></div>
                                    )}
                                </div>
                            </Segment>

                            {/* Input and Output Side by Side */}
                            <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: '15px' }}>
                                {/* Input Section */}
                                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                    <Header size="small" style={{ marginBottom: '5px' }}>Input</Header>
                                    <AceEditor
                                        mode="json"
                                        theme="monokai"
                                        value={selectedEntry.input || ''}
                                        readOnly={true}
                                        width="100%"
                                        height="100%"
                                        showPrintMargin={false}
                                        fontSize={14}
                                        wrapEnabled={true}
                                        setOptions={{ useWorker: false, wrap: true }}
                                    />
                                </div>

                                {/* Output Section */}
                                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '5px'
                                    }}>
                                        <Header size="small" style={{ margin: 0 }}>
                                            Output {isPrettified && <Label size="tiny" color="green">Prettified</Label>}
                                        </Header>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            {jsonFormatError && (
                                                <Label color="red" pointing="right">
                                                    {jsonFormatError}
                                                </Label>
                                            )}
                                            <Button
                                                size="small"
                                                onClick={handleShowOriginal}
                                                disabled={!isPrettified}
                                            >
                                                <Icon name="undo" /> See Original
                                            </Button>
                                            <Button size="small" onClick={handlePrettifyOutput}>
                                                <Icon name="code" /> Prettify JSON
                                            </Button>
                                        </div>
                                    </div>
                                    <AceEditor
                                        mode="json"
                                        theme="monokai"
                                        value={prettifiedOutput || selectedEntry.rawOutput || selectedEntry.output}
                                        readOnly={true}
                                        width="100%"
                                        height="100%"
                                        showPrintMargin={false}
                                        fontSize={14}
                                        wrapEnabled={true}
                                        setOptions={{ useWorker: false, wrap: true }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setDetailViewOpen(false)}>Close</Button>
                    <Button primary onClick={() => {
                        setDetailViewOpen(false);
                        if (selectedEntry) openChat(selectedEntry);
                    }}>
                        Open Chat
                    </Button>
                </Modal.Actions>
            </Modal>
        </>
    );
}

const SingleOrderTab = ({ itemJson, onChange }: { itemJson: string, onChange: (newJson: string) => void }) => {
    const [showAttachments, setShowAttachments] = React.useState(false);
    const [selectedAttachmentIndex, setSelectedAttachmentIndex] = React.useState(0);

    const attachments = React.useMemo(() => extractAttachments(itemJson), [itemJson]);
    const cleanedJson = React.useMemo(() => cleanJsonForDisplay(itemJson), [itemJson]);

    React.useEffect(() => {
        if (showAttachments && attachments.length === 0) {
            setShowAttachments(false);
        }
    }, [attachments.length, showAttachments]);

    const dropdownOptions = React.useMemo(
        () => attachments.map((att, idx) => ({
            key: idx,
            text: att.name,
            value: idx
        })),
        [attachments]
    );

    const handleEditorChange = React.useCallback((newCleanedJson: string) => {
        onChange(newCleanedJson);
    }, [onChange]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {attachments.length > 0 && (
                <div style={{
                    padding: '2px 8px',
                    background: '#f9f9f9',
                    borderBottom: '1px solid #ddd',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    position: 'relative',
                    zIndex: 1000,
                    fontSize: '0.85em',
                    flexShrink: 0
                }}>
                    <Checkbox
                        toggle
                        size="mini"
                        checked={showAttachments}
                        onChange={(_, data) => setShowAttachments(data.checked || false)}
                        label=""
                        style={{ margin: 0 }}
                    />
                    <Label size="mini" color={showAttachments ? 'blue' : undefined} style={{ margin: 0, padding: '3px 8px' }}>
                        {showAttachments ? 'Files' : 'JSON'} ({attachments.length})
                    </Label>

                    {showAttachments && attachments.length > 1 && (
                        <Dropdown
                            selection
                            compact
                            options={dropdownOptions}
                            value={selectedAttachmentIndex}
                            onChange={(_, data) => setSelectedAttachmentIndex(data.value as number)}
                            style={{ minWidth: '150px', fontSize: '1em' }}
                        />
                    )}
                </div>
            )}

            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {showAttachments && attachments.length > 0 ? (
                    <PdfViewer attachment={attachments[selectedAttachmentIndex]} />
                ) : (
                    <TransportOrderJsonViewer
                        transportOrder={cleanedJson}
                        readOnly={false}
                        onChange={handleEditorChange}
                    />
                )}
            </div>
        </div>
    );
};

const ClientSideMultiViewer = ({
                                   jsonContent,
                                   onChange
                               }: {
    jsonContent: string,
    onChange: (newFullJson: string) => void
}) => {
    const [viewMode, setViewMode] = React.useState<'raw' | 'split'>('raw');
    const [activeIndex, setActiveIndex] = React.useState(0);

    const { parsedData, isList, count } = React.useMemo(() => {
        try {
            const parsed = JSON.parse(jsonContent);
            if (Array.isArray(parsed)) {
                return { parsedData: parsed, isList: true, count: parsed.length };
            } else {
                return { parsedData: [parsed], isList: false, count: 1 };
            }
        } catch (e) {
            return { parsedData: null, isList: false, count: 0 };
        }
    }, [jsonContent]);

    const displayContent = React.useMemo(() => {
        return cleanJsonForDisplay(jsonContent);
    }, [jsonContent]);

    const handleItemChange = (itemIndex: number, newItemJsonString: string) => {
        if (!parsedData) return;
        try {
            const newItemObj = JSON.parse(newItemJsonString);
            const newArray = [...parsedData];
            newArray[itemIndex] = newItemObj;

            if (isList) {
                onChange(JSON.stringify(newArray, null, 2));
            } else {
                onChange(JSON.stringify(newItemObj, null, 2));
            }
        } catch (e) {
            console.error("Failed to re-assemble JSON", e);
        }
    };

    if (!parsedData) {
        return <JsonEditor content={displayContent} />;
    }

    if (viewMode === 'raw') {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                {isList && (
                    <Message info size="mini" style={{ margin: '0', padding: '5px 10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 0, borderBottom: '1px solid #cce2ff' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Icon name="list" />
                            <span>Detected List: <strong>{count}</strong> Order(s)</span>
                        </div>
                        <Button
                            size="mini"
                            color="blue"
                            compact
                            icon="columns"
                            content="Split & Optimize"
                            onClick={() => setViewMode('split')}
                        />
                    </Message>
                )}
                <div style={{ flex: 1, minHeight: 0 }}>
                    {!isList ? (
                        <SingleOrderTab
                            itemJson={jsonContent}
                            onChange={onChange}
                        />
                    ) : (
                        <JsonEditor content={displayContent} />
                    )}
                </div>
            </div>
        );
    }

    const panes = parsedData.map((item: any, index: number) => {
        const itemId = item._id || item.id || `Order ${index + 1}`;
        return {
            menuItem: `${itemId}`,
            render: () => (
                <Tab.Pane style={{ padding: 0, border: 'none', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <SingleOrderTab
                        itemJson={JSON.stringify(item, null, 2)}
                        onChange={(newVal) => handleItemChange(index, newVal)}
                    />
                </Tab.Pane>
            )
        };
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div style={{ padding: '3px 5px', borderBottom: '1px solid #ddd', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <Header as="h6" style={{ margin: 0 }}>
                    <Icon name="columns" /> Split View
                </Header>
                <Button
                    size="mini"
                    icon="arrow left"
                    content="Back"
                    onClick={() => setViewMode('raw')}
                    basic
                    compact
                />
            </div>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Tab
                    menu={{ secondary: true, pointing: true, size: 'small', style: { margin: 0, overflowX: 'auto', flexWrap: 'nowrap', flexShrink: 0 } }}
                    panes={panes}
                    activeIndex={activeIndex}
                    onTabChange={(_, data) => setActiveIndex(data.activeIndex as number)}
                    style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                    renderActiveOnly={true}
                />
            </div>
        </div>
    );
};

interface SideViewProps {
    content: string;
    originalContent: string;
    type: 'expected' | 'actual';
    attachments: Attachment[];
    showAttachments: boolean;
    setShowAttachments: (show: boolean) => void;
    selectedAttachmentIndex: number;
    setSelectedAttachmentIndex: (idx: number) => void;
    onCopy: (text: string) => void;
    onContentChange: (newContent: string) => void;
}

const SideView = React.memo(({
                                 content,
                                 originalContent,
                                 type,
                                 attachments,
                                 showAttachments,
                                 setShowAttachments,
                                 selectedAttachmentIndex,
                                 setSelectedAttachmentIndex,
                                 onCopy,
                                 onContentChange
                             }: SideViewProps) => {
    const bgColor = type === 'expected' ? '#e8f5e9' : '#ffebee';
    const textColor = type === 'expected' ? '#2e7d32' : '#c62828';
    const label = type === 'expected' ? 'Expected' : 'Actual';

    const dropdownOptions = React.useMemo(
        () => attachments.map((att, idx) => ({
            key: idx,
            text: att.name,
            value: idx
        })),
        [attachments]
    );

    const cleanedContent = React.useMemo(() => cleanJsonForDisplay(content), [content]);

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%', overflow: 'hidden' }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: bgColor,
                padding: '6px 8px',
                borderRadius: '3px',
                marginBottom: '3px',
                border: `1px solid ${type === 'expected' ? '#c8e6c9' : '#ffcdd2'}`,
                flexShrink: 0,
                position: 'relative',
                zIndex: 1000
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong style={{ color: textColor }}>{label}</strong>
                    {attachments && attachments.length > 0 && (
                        <>
                            <Checkbox
                                toggle
                                checked={showAttachments}
                                onChange={(_, data) => setShowAttachments(data.checked || false)}
                                style={{ margin: 0 }}
                            />
                            <Label size="tiny" color={showAttachments ? 'blue' : undefined}>
                                {showAttachments ? 'Files' : 'JSON'} ({attachments.length})
                            </Label>
                        </>
                    )}
                    {showAttachments && attachments.length > 1 && (
                        <Dropdown
                            selection
                            compact
                            value={selectedAttachmentIndex}
                            onChange={(_, data) => setSelectedAttachmentIndex(data.value as number)}
                            options={dropdownOptions}
                            style={{ fontSize: '0.85em', minWidth: '150px' }}
                        />
                    )}
                </div>
                {!showAttachments && <Icon name="copy" link onClick={() => onCopy(cleanedContent)} />}
            </div>

            <div style={{ flex: 1, minHeight: 0, border: '1px solid #ddd', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {showAttachments ? (
                    attachments.length > 0 ? (
                        <PdfViewer attachment={attachments[selectedAttachmentIndex]} />
                    ) : (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p style={{ color: '#999' }}>No attachments</p>
                        </div>
                    )
                ) : (
                    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <ClientSideMultiViewer
                            jsonContent={cleanedContent}
                            onChange={onContentChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
});


export function TestFailuresPage() {
    const [failures, setFailures] = React.useState<TestFailureLogEntry[]>([]);
    const [loading, setLoading] = React.useState(false);

    const [testNameFilter, setTestNameFilter] = React.useState("");
    const [errorContainsFilter, setErrorContainsFilter] = React.useState("");
    const [limit, setLimit] = React.useState(50);
    const [total, setTotal] = React.useState(0);
    const [isGrouped, setIsGrouped] = React.useState(false);
    const [selectedFailure, setSelectedFailure] = React.useState<TestFailureLogEntry | null>(null);
    const [detailsOpen, setDetailsOpen] = React.useState(false);

    const [showDiffViewer, setShowDiffViewer] = React.useState(true);
    const [showDetailedSummary, setShowDetailedSummary] = React.useState(true);

    const [showExpectedAttachments, setShowExpectedAttachments] = React.useState(false);
    const [showActualAttachments, setShowActualAttachments] = React.useState(false);
    const [selectedExpectedAttachmentIndex, setSelectedExpectedAttachmentIndex] = React.useState(0);
    const [selectedActualAttachmentIndex, setSelectedActualAttachmentIndex] = React.useState(0);

    const [currentExpected, setCurrentExpected] = React.useState("");
    const [currentActual, setCurrentActual] = React.useState("");

    const [originalExpected, setOriginalExpected] = React.useState("");
    const [originalActual, setOriginalActual] = React.useState("");

    const [aiDetailsOpen, setAiDetailsOpen] = React.useState(false);

    React.useEffect(() => {
        if (selectedFailure) {
            setOriginalExpected(selectedFailure.expected || "");
            setOriginalActual(selectedFailure.actual || "");
            setCurrentExpected(selectedFailure.expected || "");
            setCurrentActual(selectedFailure.actual || "");
        } else {
            setOriginalExpected("");
            setOriginalActual("");
            setCurrentExpected("");
            setCurrentActual("");
        }
    }, [selectedFailure]);

    const expectedAttachments = React.useMemo(() =>
            extractAttachments(originalExpected)
        , [originalExpected]);

    const actualAttachments = React.useMemo(() =>
            extractAttachments(originalActual)
        , [originalActual]);

    const diffExpected = React.useMemo(() => cleanJsonForDisplay(currentExpected), [currentExpected]);
    const diffActual = React.useMemo(() => cleanJsonForDisplay(currentActual), [currentActual]);

    const groupedFailures = React.useMemo<GroupedFailure[]>(() => {
        if (!isGrouped) return [];
        const groups: Record<string, GroupedFailure> = {};
        const now = Date.now();
        const oneDay = 24 * 60 * 60 * 1000;
        const sevenDays = 7 * oneDay;

        failures.forEach(f => {
            if (!groups[f.testName]) {
                groups[f.testName] = {
                    testName: f.testName,
                    latestFailure: f,
                    count24h: 0,
                    count7d: 0,
                    totalInView: 0
                };
            }
            const currentGroup = groups[f.testName];
            currentGroup.totalInView++;
            if (new Date(f.timestamp).getTime() > new Date(currentGroup.latestFailure.timestamp).getTime()) {
                currentGroup.latestFailure = f;
            }
            const timeDiff = now - new Date(f.timestamp).getTime();
            if (timeDiff <= oneDay) currentGroup.count24h++;
            if (timeDiff <= sevenDays) currentGroup.count7d++;
        });

        return Object.values(groups).sort((a, b) =>
            new Date(b.latestFailure.timestamp).getTime() - new Date(a.latestFailure.timestamp).getTime()
        );
    }, [failures, isGrouped]);

    const loadFailures = React.useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchTestFailures({
                testName: testNameFilter || undefined,
                errorContains: errorContainsFilter || undefined,
                limit
            });
            setFailures(data.testFailures);
            setTotal(data.total);
        } catch (e) {
            console.error("Failed to load test failures:", e);
        } finally {
            setLoading(false);
        }
    }, [testNameFilter, errorContainsFilter, limit]);

    React.useEffect(() => {
        loadFailures();
    }, []);

    const openDetails = React.useCallback((failure: TestFailureLogEntry) => {
        setSelectedFailure(failure);
        setDetailsOpen(true);
        setShowExpectedAttachments(false);
        setShowActualAttachments(false);
        setSelectedExpectedAttachmentIndex(0);
        setSelectedActualAttachmentIndex(0);
        setShowDiffViewer(true);
        setShowDetailedSummary(true);
    }, []);

    const copyToClipboard = React.useCallback((text: string) => {
        navigator.clipboard.writeText(text);
    }, []);

    const hasAiData = selectedFailure && (
        (Array.isArray(selectedFailure.actualAiProcessingData) && selectedFailure.actualAiProcessingData.length > 0) ||
        (Array.isArray(selectedFailure.expectedAiProcessingData) && selectedFailure.expectedAiProcessingData.length > 0)
    );

    const DIFF_SUMMARY_MAX_HEIGHT = '150px';

    return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', padding: '20px', boxSizing: 'border-box', overflow: 'hidden' }}>
            <Segment style={{ padding: '10px', flexShrink: 0 }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <Input
                        placeholder="Test name..."
                        value={testNameFilter}
                        onChange={(e) => setTestNameFilter(e.target.value)}
                        style={{ width: '200px' }}
                    />
                    <Input
                        placeholder="Error contains..."
                        value={errorContainsFilter}
                        onChange={(e) => setErrorContainsFilter(e.target.value)}
                        style={{ width: '200px' }}
                    />
                    <Dropdown
                        selection
                        options={LIMIT_OPTIONS}
                        value={limit}
                        onChange={(_, data) => setLimit(data.value as number)}
                        style={{ minWidth: '80px' }}
                    />
                    <Button primary onClick={loadFailures} loading={loading}>Search</Button>

                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '15px', alignItems: 'center' }}>
                        <Popup
                            content="Groups currently loaded failures by Test Name and shows frequency statistics. View shows only the latest failure per test."
                            trigger={
                                <Checkbox
                                    toggle
                                    label="Group by Test (Latest Only)"
                                    checked={isGrouped}
                                    onChange={(_, data) => setIsGrouped(data.checked || false)}
                                />
                            }
                        />
                        <Label>Total Logs: {total}</Label>
                    </div>
                </div>
            </Segment>

            <div style={{ flex: 1, overflow: 'auto', marginTop: '10px', border: '1px solid rgba(34,36,38,.15)', borderRadius: '4px' }}>
                <Table celled striped compact fixed style={{ border: 'none', margin: 0 }}>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell width={4}>Test Name</Table.HeaderCell>
                            <Table.HeaderCell width={isGrouped ? 5 : 6}>
                                {isGrouped ? 'Latest Error' : 'Error Message'}
                            </Table.HeaderCell>
                            {isGrouped ? (
                                <>
                                    <Table.HeaderCell width={1} textAlign="center">
                                        <Popup content="Failures in last 24 hours" trigger={<span>24h</span>} />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell width={1} textAlign="center">
                                        <Popup content="Failures in last 7 days" trigger={<span>7d</span>} />
                                    </Table.HeaderCell>
                                </>
                            ) : (
                                <Table.HeaderCell width={1} textAlign="center">Diffs</Table.HeaderCell>
                            )}
                            <Table.HeaderCell width={3}>
                                {isGrouped ? 'Latest Time' : 'Timestamp'}
                            </Table.HeaderCell>
                            <Table.HeaderCell width={1}></Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {isGrouped ? (
                            groupedFailures.map((group) => (
                                <Table.Row key={group.testName}>
                                    <Table.Cell style={{ fontFamily: 'monospace' }}>
                                        {group.testName}
                                    </Table.Cell>
                                    <Table.Cell error>
                                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                             title={group.latestFailure.errorMessage}>
                                            {group.latestFailure.errorMessage || '-'}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell textAlign="center">
                                        <Label size="small" circular color={group.count24h > 0 ? 'red' : 'grey'}>
                                            {group.count24h}
                                        </Label>
                                    </Table.Cell>
                                    <Table.Cell textAlign="center">
                                        <Label size="small" circular color={group.count7d > 0 ? 'orange' : 'grey'}>
                                            {group.count7d}
                                        </Label>
                                    </Table.Cell>
                                    <Table.Cell>{new Date(group.latestFailure.timestamp).toLocaleString()}</Table.Cell>
                                    <Table.Cell textAlign="center">
                                        <Button size="tiny" icon="eye" onClick={() => openDetails(group.latestFailure)} />
                                    </Table.Cell>
                                </Table.Row>
                            ))
                        ) : (
                            failures.map((failure) => (
                                <Table.Row key={failure._id}>
                                    <Table.Cell style={{ fontFamily: 'monospace' }}>
                                        {failure.testName}
                                    </Table.Cell>
                                    <Table.Cell error>
                                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                             title={failure.errorMessage}>
                                            {failure.errorMessage || '-'}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell textAlign="center">{failure.diffs?.length || 0}</Table.Cell>
                                    <Table.Cell>{new Date(failure.timestamp).toLocaleString()}</Table.Cell>
                                    <Table.Cell textAlign="center">
                                        <Button icon="eye" size="tiny" onClick={() => openDetails(failure)} />
                                    </Table.Cell>
                                </Table.Row>
                            ))
                        )}
                    </Table.Body>
                </Table>
            </div>

            <Modal
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                size="fullscreen"
                style={{ height: '90vh', overflow: 'hidden' }}
            >
                <Header style={{ backgroundColor: '#2c3e50', color: '#fff', padding: '10px 15px', flexShrink: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Failure Details</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            {hasAiData && (
                                <Button
                                    size="small"
                                    color="black"
                                    onClick={() => setAiDetailsOpen(true)}
                                >
                                    <Icon name="microchip" /> AI Details
                                </Button>
                            )}
                            {selectedFailure?.detailedDiffSummary && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '0.9rem', fontWeight: 'normal' }}>
                                        {showDetailedSummary ? 'Detailed Summary' : 'Standard Diffs'}
                                    </span>
                                    <Checkbox
                                        toggle
                                        checked={showDetailedSummary}
                                        onChange={(_, data) => setShowDetailedSummary(data.checked || false)}
                                    />
                                </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '0.9rem', fontWeight: 'normal' }}>Show Diff Viewer</span>
                                <Checkbox
                                    toggle
                                    checked={showDiffViewer}
                                    onChange={(_, data) => setShowDiffViewer(data.checked || false)}
                                />
                            </div>
                        </div>
                    </div>
                </Header>

                <Modal.Content
                    scrolling
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'calc(100% - 100px)',
                        padding: '10px',
                        backgroundColor: '#f9f9f9',
                        maxHeight: 'calc(90vh - 120px)'
                    }}
                >
                    {selectedFailure && (
                        <>
                            <Segment style={{ padding: '10px', marginBottom: '10px', flexShrink: 0 }}>
                                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '0.95em' }}>
                                    <div style={{ flex: 1 }}><strong>Test:</strong> {selectedFailure.testName}</div>
                                    <div><strong>Timestamp:</strong> {new Date(selectedFailure.timestamp).toLocaleString()}</div>
                                    <div><strong>Diffs:</strong> {selectedFailure.diffs?.length || 0}</div>
                                    {hasAiData && (
                                        <div>
                                            <strong>AI Requests:</strong>{' '}
                                            {(selectedFailure.actualAiProcessingData?.length || 0)} actual /{' '}
                                            {(selectedFailure.expectedAiProcessingData?.length || 0)} expected
                                        </div>
                                    )}
                                </div>
                                <div style={{ color: '#d32f2f', marginTop: '5px', fontSize: '0.9em', maxHeight: '42px', overflowY: 'auto' }}>
                                    {selectedFailure.errorMessage}
                                </div>
                                {selectedFailure.testSettings && (
                                    <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px', fontSize: '0.85em' }}>
                                        {selectedFailure.testSettings.fieldsToIgnore && selectedFailure.testSettings.fieldsToIgnore.length > 0 && (
                                            <div style={{ marginBottom: '4px' }}>
                                                <strong>Fields to Ignore:</strong> {selectedFailure.testSettings.fieldsToIgnore.join(', ')}
                                            </div>
                                        )}
                                        {selectedFailure.testSettings.fieldsToFuzzyMatch && Object.keys(selectedFailure.testSettings.fieldsToFuzzyMatch).length > 0 && (
                                            <div>
                                                <strong>Fuzzy Match:</strong> {Object.entries(selectedFailure.testSettings.fieldsToFuzzyMatch)
                                                .map(([field, threshold]) => `${field} (±${threshold})`)
                                                .join(', ')}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Segment>

                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                                {showDiffViewer ? (
                                    <div style={{ flex: 1, border: '1px solid #ccc', borderRadius: '4px', overflow: 'auto' }}>
                                        <CustomDiff oldJson={diffExpected} newJson={diffActual} />
                                        <div style={{ padding: '10px', textAlign: 'center', background: '#f0f0f0', fontSize: '0.9em', color: '#666' }}>
                                            <Icon name="info circle" />
                                            Toggle "Show Diff Viewer" off to access optimization controls in the side-by-side view.
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', gap: '10px', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                        <SideView
                                            content={currentExpected}
                                            originalContent={originalExpected}
                                            type="expected"
                                            attachments={expectedAttachments}
                                            showAttachments={showExpectedAttachments}
                                            setShowAttachments={setShowExpectedAttachments}
                                            selectedAttachmentIndex={selectedExpectedAttachmentIndex}
                                            setSelectedAttachmentIndex={setSelectedExpectedAttachmentIndex}
                                            onCopy={copyToClipboard}
                                            onContentChange={setCurrentExpected}
                                        />
                                        <SideView
                                            content={currentActual}
                                            originalContent={originalActual}
                                            type="actual"
                                            attachments={actualAttachments}
                                            showAttachments={showActualAttachments}
                                            setShowAttachments={setShowActualAttachments}
                                            selectedAttachmentIndex={selectedActualAttachmentIndex}
                                            setSelectedAttachmentIndex={setSelectedActualAttachmentIndex}
                                            onCopy={copyToClipboard}
                                            onContentChange={setCurrentActual}
                                        />
                                    </div>
                                )}
                            </div>

                            {showDetailedSummary && selectedFailure.detailedDiffSummary ? (
                                <div style={{ marginTop: '10px', flexShrink: 0 }}>
                                    <Header as='h5' attached='top' block style={{ padding: '5px 10px', margin: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>Detailed Diff Summary</span>
                                        <Icon name="copy" link onClick={() => copyToClipboard(selectedFailure.detailedDiffSummary || '')} />
                                    </Header>
                                    <Segment attached style={{
                                        padding: '10px',
                                        maxHeight: DIFF_SUMMARY_MAX_HEIGHT,
                                        overflowY: 'auto',
                                        margin: 0
                                    }}>
                                        <pre style={{ margin: 0, fontSize: '0.85em', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#333' }}>
                                            {selectedFailure.detailedDiffSummary}
                                        </pre>
                                    </Segment>
                                </div>
                            ) : (
                                selectedFailure.diffs && selectedFailure.diffs.length > 0 && (
                                    <div style={{ marginTop: '10px', flexShrink: 0 }}>
                                        <Header as='h5' attached='top' block style={{ padding: '5px 10px', margin: 0 }}>
                                            Differences List ({selectedFailure.diffs.length})
                                        </Header>
                                        <Segment attached style={{
                                            padding: '10px',
                                            maxHeight: DIFF_SUMMARY_MAX_HEIGHT,
                                            overflowY: 'auto',
                                            margin: 0
                                        }}>
                                            {selectedFailure.diffs.map((diff, idx) => (
                                                <div key={idx} style={{
                                                    marginBottom: '8px',
                                                    paddingBottom: '8px',
                                                    borderBottom: idx < (selectedFailure.diffs?.length || 0) - 1 ? '1px solid #eee' : 'none'
                                                }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                                                        <Label size="tiny">Diff #{idx + 1}</Label>
                                                        <Icon name="copy" link onClick={() => copyToClipboard(diff)} />
                                                    </div>
                                                    <pre style={{ margin: 0, fontSize: '0.85em', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#444' }}>
                                                        {diff}
                                                    </pre>
                                                </div>
                                            ))}
                                        </Segment>
                                    </div>
                                )
                            )}
                        </>
                    )}
                </Modal.Content>
                <Modal.Actions style={{ padding: '5px', flexShrink: 0 }}>
                    <Button onClick={() => setDetailsOpen(false)}>Close</Button>
                </Modal.Actions>
            </Modal>

            <AiProcessingDetailsModal
                open={aiDetailsOpen}
                onClose={() => setAiDetailsOpen(false)}
                expectedData={selectedFailure?.expectedAiProcessingData}
                actualData={selectedFailure?.actualAiProcessingData}
            />
        </div>
    );
}